/**
 *
 * TeacherCommunicationDetailMobile
 *
 */

import React, { memo, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from "react-i18next";

// styles
import './style.scss';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import reducer from './reducer';
import saga from './saga';

import { flushState, loadCommunication, updateCommunication } from './actions';
import {
  // other
  makeSelectLoading,
  makeSelectError,
  makeSelectItemDetail,
  makeSelectUpdateLoading,
  makeSelectFirstRender,
} from './selectors';

// antd component
import { Layout, Empty } from 'antd';
const { Content } = Layout;

// assets
import {
  ArrowLeftOutlined,
} from '@ant-design/icons';
import CustomIcons from 'components/CustomIcons';

// components
import Preloader from 'components/Preloader';

// Communication components
import { CommunicationPreview } from 'components/Communications';
import { FBDocumentsPreview } from 'components/Communications';

import { DiscussionMobile } from 'mobile/components/Communications';

// hooks
import { useRefState } from 'utils/hooks';
// utils
import { parseQuery } from 'utils/history';
import { isDateBeforeDateBySeconds } from 'utils/moment/moment.utils';
import _local from 'utils/localStorageUtils';

export function TeacherCommunicationDetailMobile({
  // main data
  itemDetail,
  // states
  history,
  error,
  loading,
  firstRender,
  updateLoading,
  // actions
  loadCommunication,
  updateCommunication,
  flushState,
}) {
  useInjectReducer({ key: 'TeacherCommunicationDetailMobile', reducer });
  useInjectSaga({ key: 'TeacherCommunicationDetailMobile', saga });

  const { t, i18n } = useTranslation();

  const { ID: initId } = useParams();
  const location = useLocation();
  const searchParams = parseQuery(location.search) || null;

  const localUser = _local.getLocalUser();

  const [discussionTab, setDiscussionTab] = useState(false);
  const [currentElement, currentElementRef, setCurrentElemente] = useRefState(
    itemDetail,
  );

  const oldParams = history.location?.state?.oldParams;
  const goBack = history.location?.state?.goBack;
  const goBackTitle = history.location?.state?.goBackTitle;

  useEffect(() => {
    if (initId) {
      loadCommunication(initId, true);
    }

    return () => {
      setCurrentElemente({});
      // Anything in here is fired on component unmount.
      flushState();
    };
  }, []);

  // Get Comunication Data
  useEffect(() => {
    if (!!searchParams?.IDComm) {
      loadCommunication(searchParams?.IDComm, true);
    }
  }, [searchParams?.IDComm]);

  useEffect(() => {
    if (itemDetail?._id) {
      setCurrentElemente({ ...currentElement, ...itemDetail });

      const hasThisUser = !!itemDetail?.recipientsActions && itemDetail?.recipientsActions?.find(
        el => el?.recipient == localUser?._id,
      );

      const adminAnserIsActual = isDateBeforeDateBySeconds(itemDetail?.adminAnswer?.adminAnswerDate, itemDetail?.adminAnswerReadDate);
      const setAdminAnswerReadDate = !!adminAnserIsActual || !!itemDetail?.adminAnswer && !!!itemDetail?.adminAnswerReadDate;

      if (!!firstRender) {
        if (!itemDetail?.read || !!!hasThisUser || !!setAdminAnswerReadDate) {
          // set as Read
          const recipientsActions = itemDetail?.recipientsActions || [];
          const userArr = hasThisUser
            ? itemDetail?.recipientsActions
            : [
                ...recipientsActions,
                {
                  recipient: localUser?._id,
                  responseID: initId,
                  communication: itemDetail?.communication,
                  read: true,
                  readDate: new Date().toISOString(),
                },
              ];
  
          let bodyObj = {
            _ID: initId,
            read: true,
            readDate: new Date().toISOString(),
            recipientsActions: userArr,
          };
          if (!!setAdminAnswerReadDate) {
            bodyObj = {
              ...bodyObj,
              adminAnswerReadDate: new Date().toISOString(),
            };
          }
  
          updateCommunication(bodyObj);

        }
      }
      
    }
  }, [itemDetail]);

  const renderDiscussionPage = useMemo(() => {
    return (
      <Content className="main-content-container communication-detail discussion-opened">
        <div className="communication-detail__header">
          <div className="go-back" onClick={() => setDiscussionTab(false)}>
            <ArrowLeftOutlined />{" "}
            {(itemDetail?.title && (
              <>
                {t("labels.communication")}:{" "}
                <span className="title">{itemDetail?.title}</span>
              </>
            )) ||
              t("labels.communication")}
          </div>
        </div>
        <div className="communication-detail__main">
          <DiscussionMobile
            discussionId={currentElement?.discussion}
            communicationId={currentElement._id}
          />
        </div>
      </Content>
    );
  }, [itemDetail, currentElement, initId, i18n?.language]);

  const renderTopPageHeader = useMemo(() => {
    return (
      <div className="communication-detail__header">
        <div
          className="go-back"
          onClick={() => {
            history.push({
              pathname: goBack ? goBack : `/communications`,
              search: "",
              hash: "",
              state: {
                oldParams: oldParams,
                goBack: "/communications",
                cameFrom: "single_communication",
                needRefresh: searchParams?.needRefresh,
              },
            });
          }}
        >
          <ArrowLeftOutlined />{" "}
          {goBackTitle ? goBackTitle : t("labels.communications")}
        </div>
        {!!itemDetail?.discussion && (
          <div className="go-chat" onClick={() => setDiscussionTab(true)}>
            <CustomIcons type="comments" />
          </div>
        )}
      </div>
    );
  }, [goBack, oldParams, goBackTitle, searchParams, itemDetail?.discussion, i18n?.language]);

  
  // render function
  return (
    <Layout className="main-full-content">
      <Helmet>
        <title>{t("labels.communication")}</title>
        <meta name="description" content="Description of Communication" />
      </Helmet>
      {discussionTab ? (
        renderDiscussionPage
      ) : (
        <Content className="main-content-container communication-detail">
          {renderTopPageHeader}
          <div className="communication-detail__main">
            {loading && !currentElement && <Preloader />}
            {!loading && currentElement && (
              <>
                <CommunicationPreview
                  element={currentElement}
                  hideForm={true}
                  hideAppointment={true}
                  hideDocument={true}
                  hideDiscussion={false}
                  mode="onlyRead"
                  onViewDiscussionClick={() => setDiscussionTab(true)}
                />
                {currentElement?.documents && (
                  <FBDocumentsPreview documents={currentElement?.documents} />
                )}
              </>
            )}
            {!loading && !currentElement && (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t("errors.noData")}
              />
            )}
          </div>
        </Content>
      )}
    </Layout>
  );
}

TeacherCommunicationDetailMobile.propTypes = {
  dispatch: PropTypes.func.isRequired,
  flushState: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

const mapStateToProps = createStructuredSelector({
  loading: makeSelectLoading(),
  error: makeSelectError(),
  itemDetail: makeSelectItemDetail(),
  updateLoading: makeSelectUpdateLoading(),
  firstRender: makeSelectFirstRender(),
});

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    flushState: () => dispatch(flushState()),
    loadCommunication: (commObjID, firstRender) => dispatch(loadCommunication(commObjID, firstRender)),
    updateCommunication: itemData => dispatch(updateCommunication(itemData)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(TeacherCommunicationDetailMobile);
